/**
 * Google Tag Manager "data layer" helpers.
 *
 * "Data layer" is Google Tag Manager terminology: a single global array,
 * `window.dataLayer`, that the GTM container script continuously watches.
 * Pushing an object onto it is how the page hands data and events to GTM, which
 * then routes them on to GA4 and any other configured tags.
 * https://developers.google.com/tag-platform/tag-manager/datalayer
 *
 * This module is the one place the app touches that global. Features report
 * through `pushEvent` / `pushOnce` rather than reaching for `window.dataLayer`
 * directly, so what we hand GTM stays consistent.
 */

declare global {
  interface Window {
    // Created by the GTM snippet; typed here so the app can push to it safely.
    dataLayer?: Array<Record<string, unknown>>
  }
}

// Keys already handled by pushOnce, for the lifetime of the page.
const pushedEvents = new Set<string>()

// Lazily create and return the global GTM data layer array.
export function dataLayer(): Array<Record<string, unknown>> {
  window.dataLayer = window.dataLayer || []
  return window.dataLayer
}

// Push a named event onto the data layer. A GTM "Custom Event" trigger fires on
// the `event` name; `params` become data layer variables GTM can read and forward.
export function pushEvent(event: string, params: Record<string, unknown> = {}): void {
  dataLayer().push({
    event,
    ...params,
  })
}

// Run `callback` at most once per page load for a given key. Guards against
// pushing the same event twice, e.g. a re-render firing an exposure event again.
export function pushOnce(key: string, callback: () => void): void {
  if (pushedEvents.has(key)) return

  pushedEvents.add(key)
  callback()
}
